// src/tenantAdmin/components/RenameWorkspaceDialog.jsx
//
// "Rinomina workspace" — PATCH /tenant-admin/tenant, 1–100 characters.
//
// ⚠️⚠️ TWO things the hint has to say, because neither is guessable:
//   1. The name is ALSO the brand-identity company name the generator uses for
//      this workspace (the backend re-syncs it on rename), so this is not a label
//      change — it changes what comes out of the pipeline.
//   2. `tenants[]` is minted at sign-in and there is no claims-refresh route, so
//      the new name reaches the rest of the app only from the next sign-in. The
//      caller patches it locally (renameTenantLocally) and says so; until then a
//      reload shows the old name again, which is accepted (plan decision 6).
//
// Like CorrectEmailDialog this component knows no route: `onSubmit(name)` does the
// write. ⛔ Refusals are worded from `err.code` (INVALID_DISPLAY_NAME → the 1–100
// sentence), never from `err.message`.
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdminModal from '../../admin/components/AdminModal.jsx';
import { userActionErrorCopy } from '../userActionError.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

const fieldLabel = {
  display: 'block', fontSize: '11px', fontWeight: 600, letterSpacing: '0.6px',
  textTransform: 'uppercase', color: COLORS.textSecondary, fontFamily: FONT_SANS,
  marginBottom: '8px',
};
const field = {
  width: '100%', height: '46px', boxSizing: 'border-box',
  background: COLORS.surface, border: `1px solid ${COLORS.border}`,
  borderRadius: RADII.control, color: COLORS.ink, fontSize: '14px',
  fontFamily: FONT_SANS, outline: 'none', padding: '0 14px',
};

export default function RenameWorkspaceDialog({ open, currentName, onSubmit, onClose }) {
  const { t } = useTranslation('admin');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(currentName || '');
    setError('');
    setLoading(false);
  }, [open, currentName]);

  const next = name.trim();
  // An unchanged name would be a write with nothing to show for it.
  const dirty = next !== '' && next !== (currentName || '');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!dirty || loading) return;
    setError('');
    setLoading(true);
    try {
      await onSubmit(next);
      onClose();
    } catch (err) {
      setError(userActionErrorCopy(t, err));
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    if (loading) return;
    onClose();
  }

  return (
    <AdminModal open={open} onClose={handleClose} title={t('manage.renameTitle')} width={460}>
      <form onSubmit={handleSubmit}>
        <label htmlFor="rwd-name" style={fieldLabel}>{t('shared.name')}</label>
        {/* maxLength mirrors the backend's 100; the 1-char end is the dirty gate. */}
        <input
          id="rwd-name"
          type="text"
          value={name}
          maxLength={100}
          onChange={e => setName(e.target.value)}
          style={field}
        />
        <p style={{
          margin: '8px 0 0', fontSize: '11px', lineHeight: 1.5,
          color: COLORS.textTertiary, fontFamily: FONT_SANS,
        }}>
          {t('manage.renameHint')}
        </p>

        {error && (
          <div role="alert" style={{
            marginTop: '16px', padding: '10px 14px', borderRadius: RADII.controlInner,
            background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.fg}33`,
            color: SEMANTIC.errore.fg, fontSize: '13px', fontFamily: FONT_SANS,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            style={{
              height: '40px', padding: '0 18px', borderRadius: RADII.controlInner,
              border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
              color: COLORS.ink, fontSize: '13px', fontFamily: FONT_SANS,
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1,
            }}
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading || !dirty}
            style={{
              height: '40px', padding: '0 18px', borderRadius: RADII.controlInner, border: 'none',
              background: (loading || !dirty) ? COLORS.borderStrong : COLORS.accent,
              color: COLORS.surface, fontSize: '13px', fontWeight: 600, fontFamily: FONT_SANS,
              cursor: (loading || !dirty) ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? t('profile.saving') : t('common:actions.save')}
          </button>
        </div>
      </form>
    </AdminModal>
  );
}
